'use client';

import React, { useCallback, useMemo, useState } from 'react';
import type { Reminder } from '@/core/domain';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import { SectionScreenLayout } from '@/features/app/presentation/components/SectionScreenLayout';
import { ScheduledMaintenanceCalendar } from '@/features/app/presentation/components/ScheduledMaintenanceCalendar';
import {
  MANUTENCOES_AGENDADAS_DAY_EMPTY,
  MANUTENCOES_AGENDADAS_EMPTY,
  MANUTENCOES_AGENDADAS_HINT_SELECT_DAY,
  MANUTENCOES_AGENDADAS_LABEL_CLIENT,
  MANUTENCOES_AGENDADAS_LABEL_SCHEDULED_FOR,
  MANUTENCOES_AGENDADAS_LABEL_VALUE,
  MANUTENCOES_AGENDADAS_LOADING,
  MANUTENCOES_AGENDADAS_SECTION_SUBTITLE,
  MANUTENCOES_AGENDADAS_SECTION_TITLE,
} from '@/features/app/presentation/constants/manutencoes-agendadas-copy';
import { LEMBRETES_CLIENT_FALLBACK } from '@/features/app/presentation/constants/lembretes-calendario-copy';
import {
  filterScheduledMaintenanceReminders,
  isoToDateKey,
  scheduledMaintenanceOnDay,
} from '@/features/app/presentation/utils/reminder-day-metrics';
import { useReminders } from '@/features/app/reminders/presentation/context/ReminderContext';
import { useClients } from '@/features/app/clients/presentation/context/ClientContext';
import { useAwaitInitialFetch } from '@/features/app/presentation/hooks/use-await-initial-fetch';

function formatDatePt(iso: string): string {
  if (!iso) return '—';
  const dayKey = iso.trim().length >= 10 ? iso.trim().slice(0, 10) : iso.trim();
  return new Date(`${dayKey}T12:00:00`).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export const ManutencoesAgendadasSection: React.FC = () => {
  const { state: reminderState, events: reminderEvents } = useReminders();
  const { state: clientState, events: clientEvents } = useClients();
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);

  const loadSection = useCallback(async () => {
    await Promise.all([reminderEvents.getByUserId(), clientEvents.getByUserId()]);
  }, [reminderEvents.getByUserId, clientEvents.getByUserId]);
  const sectionDataReady = useAwaitInitialFetch(loadSection, [loadSection]);

  const agendadas = useMemo(() => {
    const list = filterScheduledMaintenanceReminders(reminderState.reminders);
    return [...list].sort((a, b) => {
      const ka = isoToDateKey(a.scheduled_date!);
      const kb = isoToDateKey(b.scheduled_date!);
      return ka.localeCompare(kb);
    });
  }, [reminderState.reminders]);

  const getClientName = useCallback(
    (clientId: string): string => {
      const client = clientState.clients.find((c) => c.id === clientId);
      return client?.name ?? LEMBRETES_CLIENT_FALLBACK;
    },
    [clientState.clients]
  );

  const agendamentosDoDia = useMemo(() => {
    if (!selectedDayKey) return [];
    return scheduledMaintenanceOnDay(agendadas, selectedDayKey);
  }, [agendadas, selectedDayKey]);

  const listLoading = !sectionDataReady;
  const hasAny = agendadas.length > 0;

  const belowLegend = useMemo(() => {
    if (!selectedDayKey) {
      return (
        <p className="text-sm text-text-muted leading-relaxed pt-1">{MANUTENCOES_AGENDADAS_HINT_SELECT_DAY}</p>
      );
    }
    return (
      <div className="space-y-3 pt-2 min-w-0">
        <p className="text-sm font-medium text-navy-dark">{formatDatePt(selectedDayKey)}</p>
        {agendamentosDoDia.length === 0 ? (
          <p className="text-sm text-text-muted">{MANUTENCOES_AGENDADAS_DAY_EMPTY}</p>
        ) : (
          <ul className="space-y-3 min-w-0 list-none pl-0">
            {agendamentosDoDia.map((r) => (
              <ScheduledMaintenanceReadOnlyCard key={r.id} reminder={r} getClientName={getClientName} />
            ))}
          </ul>
        )}
      </div>
    );
  }, [selectedDayKey, agendamentosDoDia, getClientName]);

  return (
    <SectionScreenLayout title={MANUTENCOES_AGENDADAS_SECTION_TITLE} subtitle={MANUTENCOES_AGENDADAS_SECTION_SUBTITLE}>
      <div className="space-y-4 min-w-0 overflow-hidden">
        {reminderState.error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {reminderState.error}
          </div>
        )}

        {listLoading ? (
          <p className="text-text-muted text-sm">{MANUTENCOES_AGENDADAS_LOADING}</p>
        ) : !hasAny ? (
          <div className="rounded-xl border border-border-light bg-[#fafbfc] p-8 text-center">
            <AppIcon name="CalendarDaysIcon" size={40} className="mx-auto text-text-muted mb-3" />
            <p className="text-text-muted text-sm leading-relaxed max-w-md mx-auto">{MANUTENCOES_AGENDADAS_EMPTY}</p>
          </div>
        ) : (
          <ScheduledMaintenanceCalendar
            scheduledReminders={agendadas}
            selectedDayKey={selectedDayKey}
            onSelectDay={setSelectedDayKey}
            belowLegend={belowLegend}
          />
        )}
      </div>
    </SectionScreenLayout>
  );
};

function ScheduledMaintenanceReadOnlyCard({
  reminder: r,
  getClientName,
}: {
  reminder: Reminder;
  getClientName: (id: string) => string;
}) {
  const scheduled = r.scheduled_date ? formatDatePt(r.scheduled_date) : '—';
  const showValue = r.value_charged != null && Number.isFinite(r.value_charged);

  return (
    <li className="rounded-xl border border-border-light bg-white p-4 sm:p-5 min-w-0">
      <div className="flex items-start gap-2 flex-wrap">
        <AppIcon name="UserCircleIcon" size={20} className="text-cyan shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-semibold text-navy-dark">
            <span className="text-text-muted font-normal text-xs uppercase tracking-wide mr-2">
              {MANUTENCOES_AGENDADAS_LABEL_CLIENT}
            </span>
            {getClientName(r.client_id)}
          </p>
          <p className="text-sm text-navy-dark flex items-center gap-2 flex-wrap">
            <AppIcon name="CalendarDaysIcon" size={16} className="text-emerald-600 shrink-0" />
            <span>
              {MANUTENCOES_AGENDADAS_LABEL_SCHEDULED_FOR} <span className="font-medium">{scheduled}</span>
            </span>
          </p>
          {showValue && (
            <p className="text-sm text-navy-dark flex items-center gap-2">
              <AppIcon name="BanknotesIcon" size={16} className="text-cyan shrink-0" />
              <span>
                {MANUTENCOES_AGENDADAS_LABEL_VALUE}:{' '}
                <span className="font-medium">
                  {(r.value_charged as number).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </span>
              </span>
            </p>
          )}
          {r.equipment_reference && (
            <p className="text-xs text-text-muted flex items-center gap-1 pt-1">
              <AppIcon name="TagIcon" size={14} />
              {r.equipment_reference}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}
